import { useCallback } from 'react';
import { cancelFunctions } from 'utils/cancelFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { Store } from 'components/common/types/Store.types';
import { getReferenceData } from 'modules/api/lookups';
import { changeReferenceData } from 'store/userOptions/reducer';
import { ReferenceOptionsResponse } from 'store/userOptions/reducer.types';
import { LocalLoading } from 'store/dealManagement/types';
import { changeFormParams, startLoading, stopLoading } from 'store/dealManagement/reducer';
import { DirectSalesMediaType } from 'components/common/types/DirectSalesCampaign.types';
import { AssetTypes } from 'components/common/types/AssetList.types';
import { getIsReseller, getMarketId } from 'store/publisher/selectors';
import multiPublisherRequest from 'modules/api/multiPublisherRequest';
import { getResellerDealMediaOwners } from 'store/reseller/selectors';
import { getIsDirectSalesCampaignType } from 'store/dealManagement/selectors/getCampaignType';
import {
  transformFormat,
  transformLocation,
  transformDirectSalesTags,
  transformResellerAirportTags,
} from '../PlannerSections/FiltersContent/utils';
import {
  directSalesLocationCategories,
  LoadReferenceDataOptions,
  programmaticLocationCategories,
  UseReferenceData,
} from './UseReferenceData.types';

const useReferenceData: UseReferenceData = ({ isDirectSales } = { isDirectSales: false }) => {
  const dispatch = useDispatch();
  const market = useSelector(getMarketId);
  const mediaOwnerMeta = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.mediaOwnerMeta);
  const resellerMediaOwners = useSelector(getResellerDealMediaOwners);

  const isSimplifiedPlanningMode = useSelector(
    (state: Store) => state.dealManagement.plannerSettings.isSimplifiedPlanningMode,
  );
  const isReseller = useSelector(getIsReseller);
  const isDirectSalesCampaignType = useSelector(getIsDirectSalesCampaignType);
  const environmentId = useSelector((state: Store) => state.environment.environmentId);

  const effectiveIsDirectSales = isDirectSales ?? isDirectSalesCampaignType;

  const getDirectSalesReferenceData = async ({
    environments,
    mediaType,
    startDate,
    endDate,
  }: LoadReferenceDataOptions): Promise<void> => {
    const response = await getReferenceData(cancelFunctions, {
      market,
      channelCodes: environments?.map((environment) => environment.code),
      assetTypes: mediaType ? [mediaType === DirectSalesMediaType.PAPER ? AssetTypes.STATIC : mediaType] : [],
      startDate,
      endDate,
    });

    const { format, location, tagGroup } = response;
    const { address, geographicalAreaLevel1, geographicalAreaLevel2, geographicalAreaLevel3, ...filteredLocation } =
      location;

    dispatch(
      changeReferenceData({
        tags: transformDirectSalesTags(tagGroup),
        format: transformFormat(format),
        location: transformLocation([{ location: filteredLocation }], directSalesLocationCategories),
      }),
    );
  };

  const getResellerReferenceData = async ({ startDate, endDate }: LoadReferenceDataOptions): Promise<void> => {
    const shouldFetchReferenceData =
      (mediaOwnerMeta?.map(({ code }) => code) || []).length > 0 || isSimplifiedPlanningMode;

    if (!shouldFetchReferenceData) return;

    const response = (await multiPublisherRequest(
      environmentId,
      resellerMediaOwners,
      (params) => getReferenceData({}, { ...params.payload, market: params.envId }),
      {
        payload: {
          assetTypes: [AssetTypes.DIGITAL],
          startDate,
          endDate,
        },
      },
    )) as (ReferenceOptionsResponse & { publisher: string })[];

    dispatch(
      changeReferenceData({
        location: transformLocation(response, programmaticLocationCategories, true),
        tags: transformResellerAirportTags(response.map((publisherResponse) => publisherResponse.tagGroup)),
      }),
    );
  };

  const getProgrammaticReferenceData = async ({
    environments,
    startDate,
    endDate,
  }: LoadReferenceDataOptions): Promise<void> => {
    const response = await getReferenceData(cancelFunctions, {
      market,
      channelCodes: environments?.map((env) => env.code),
      assetTypes: [AssetTypes.DIGITAL],
      startDate,
      endDate,
    });
    const { tagGroup } = response;
    dispatch(
      changeReferenceData({ format: transformFormat(response.format), tags: transformDirectSalesTags(tagGroup) }),
    );
    dispatch(
      changeReferenceData({
        location: transformLocation([{ ...response, publisher: market }], programmaticLocationCategories),
      }),
    );
  };

  const getProductTemplateReferenceData = async ({
    environments,
    mediaType,
  }: LoadReferenceDataOptions): Promise<void> => {
    const response = await getReferenceData(cancelFunctions, {
      market,
      channelCodes: environments?.map((env) => env.code),
      assetTypes: mediaType ? [mediaType === DirectSalesMediaType.PAPER ? AssetTypes.STATIC : mediaType] : [],
    });

    const { format, location, tagGroup } = response;
    const { address, geographicalAreaLevel1, geographicalAreaLevel2, geographicalAreaLevel3, ...filteredLocation } =
      location;

    dispatch(
      changeReferenceData({
        tags: transformDirectSalesTags(tagGroup),
        format: transformFormat(format),
        location: transformLocation([{ location: filteredLocation }], directSalesLocationCategories),
      }),
    );
  };

  const loadReferenceData = useCallback(
    async ({
      environments,
      mediaType,
      startDate,
      endDate,
      isProductTemplate,
    }: LoadReferenceDataOptions): Promise<boolean> => {
      if (!(startDate && endDate) && !isProductTemplate && !isSimplifiedPlanningMode) return false;
      if (!isReseller && !environments?.length && !isSimplifiedPlanningMode) return false;
      if (effectiveIsDirectSales && !mediaType) return false;

      dispatch(startLoading(LocalLoading.GET_REFERENCE_DATA));
      dispatch(changeFormParams({ isFetchingReferences: true }));

      try {
        if (effectiveIsDirectSales) {
          await getDirectSalesReferenceData({
            environments,
            mediaType,
            startDate,
            endDate,
          });
        } else if (isProductTemplate) {
          await getProductTemplateReferenceData({ environments, mediaType });
        } else if (isReseller) {
          await getResellerReferenceData({ startDate, endDate });
        } else {
          await getProgrammaticReferenceData({
            environments,
            startDate,
            endDate,
          });
        }
      } catch {
        return false;
      } finally {
        dispatch(stopLoading(LocalLoading.GET_REFERENCE_DATA));
        dispatch(changeFormParams({ isFetchingReferences: false }));
      }

      return true;
    },
    [effectiveIsDirectSales, isReseller, isSimplifiedPlanningMode, mediaOwnerMeta],
  );

  return { loadReferenceData };
};

export default useReferenceData;
